/**
 * Creates a Proxy around an array-like object (e.g. GTM DataLayer) that logs
 * all mutations (push, splice, index assignment) for debugging.
 *
 * @param {Array} [target=[]] - The array to wrap. Defaults to a new empty array.
 * @param {Object} [options] - Configuration options
 * @param {function} [options.logger=console.log] - Function to call with log messages. Signature: (message, data?)
 * @param {string} [options.prefix='[DataLayer]'] - Prefix for log messages
 * @returns {Array} - Proxy that behaves like the original array but logs changes
 *
 * @example
 * // Wrap existing dataLayer for debugging
 * window.dataLayer = createDataLayerProxy(window.dataLayer);
 *
 * // With custom logger (e.g. send to analytics debug endpoint)
 * window.dataLayer = createDataLayerProxy([], {
 *   prefix: '[GTM]',
 *   logger: (msg, data) => { console.debug(msg, data); }
 * });
 */
export function createDataLayerProxy(target = [], options = {}) {
    const { logger = console.log.bind(console), prefix = "[DataLayer]" } =
        options;

    const log = (action, detail) => {
        const message = `${prefix} ${action}`;
        if (detail !== undefined) {
            logger(message, detail);
        } else {
            logger(message);
        }
    };

    return new Proxy(Array.isArray(target) ? target : Array.from(target), {
        get(arr, prop, receiver) {
            const value = Reflect.get(arr, prop, receiver);
            if (prop === "push") {
                return function push(...args) {
                    const result = Array.prototype.push.apply(arr, args);
                    log("push", args.length === 1 ? args[0] : args);
                    return result;
                };
            }
            if (prop === "splice") {
                return function splice(start, deleteCount, ...items) {
                    const result = Array.prototype.splice.apply(arr, [
                        start,
                        deleteCount,
                        ...items,
                    ]);
                    log("splice", {
                        start,
                        deleteCount,
                        inserted: items,
                        removed: result,
                        newLength: arr.length,
                    });
                    return result;
                };
            }
            if (prop === "unshift") {
                return function unshift(...args) {
                    const result = Array.prototype.unshift.apply(arr, args);
                    log("unshift", args.length === 1 ? args[0] : args);
                    return result;
                };
            }
            if (typeof value === "function") {
                return value.bind(arr);
            }
            return value;
        },

        set(arr, prop, value, receiver) {
            const numericKey = Number(prop);
            if (Number.isInteger(numericKey) && numericKey >= 0) {
                const previous = arr[numericKey];
                Reflect.set(arr, prop, value, receiver);
                log("set index", { index: numericKey, previous, value });
                return true;
            }
            return Reflect.set(arr, prop, value, receiver);
        },
    });
}

/**
 * Replaces window.dataLayer with a proxied version that logs all pushes and
 * other mutations. Useful for local/debug builds.
 * Call early (e.g. in index.js or before any GTM script runs).
 *
 * @param {Object} [options] - Same options as createDataLayerProxy
 * @returns {Array} - The proxied dataLayer now assigned to window.dataLayer
 */
export function installDataLayerLogger(options = {}) {
    const existing = typeof window !== "undefined" ? window.dataLayer : null;
    const base = Array.isArray(existing) ? existing : [];
    const proxy = createDataLayerProxy(base, options);
    if (typeof window !== "undefined") {
        window.dataLayer = proxy;
    }
    return proxy;
}
